import { Appointment, AppointmentOutcomeEnum } from '../../types';

// Mirrored from fielmann-lt-booking-fe-server/src/data-service/types.ts
export type CustomerSearchRow = {
  id: string;
  name: string;
  lastName: string;
  phoneNumber?: string;
  email?: string;
  hasActiveSeries: boolean;
  activeSeriesId?: string;
};

export type SearchCustomersPayload = {
  q: string;
};

// Mirrored from fielmann-lt-booking-fe-server/src/data-service/types.ts
export enum SeriesStatusEnum {
  CLOSED = 0,
  OPEN = 1,
}

// Mirrored from fielmann-lt-booking-fe-server/src/data-service/types.ts
export enum SeriesOutcomeEnum {
  BOUGHT = 1,
  FAILED = 2,
}

// Mirrored from fielmann-lt-booking-fe-server/src/data-service/types.ts
export enum SeriesWarningEnum {
  FOLLOWUP_MISSING = 1,
  OUTCOME_MISSING = 2,
  RATIO_EXCEEDED = 3,
}

// Mirrored from fielmann-lt-booking-fe-server/src/data-service/types.ts
export type Series = {
  id: string;
  enterpriseId: string;
  storeId: string;
  type: number;
  customerId: string;
  rootEventId?: string;
  employeeId?: string;
  status: SeriesStatusEnum;
  outcome?: SeriesOutcomeEnum;
  note?: string;
  closedAt?: number;
  lastAppointmentAt?: number;
  createdAt: number;
  updatedAt: number;
};

export type SeriesWithEvents = {
  series: Series;
  events: Appointment[];
};

// Mirrored from fielmann-lt-booking-fe-server/src/data-service/types.ts.
export type SeriesIndexEventRaw = {
  id: string;
  dateTime: number;
  duration: number;
  outcome?: AppointmentOutcomeEnum;
  serviceIds: string[];
};

// Store-local shape
export type SeriesIndexEvent = {
  id: string;
  date: string;
  time: number;
  duration: number;
  outcome?: AppointmentOutcomeEnum;
  serviceIds: string[];
};

// Mirrored from fielmann-lt-booking-fe-server/src/data-service/types.ts
// (originally from fielmann-lt-booking-coordinator/src/search/types.ts).
export type SeriesIndexDocumentRaw = {
  id: string;
  type: 'series';
  seriesId: string;
  enterpriseId: string;
  storeId: string;
  customerId: string;
  customerName: string;
  customerLastName: string;
  customerPhoneNumber?: string;
  customerEmail?: string;
  status: SeriesStatusEnum;
  outcome?: SeriesOutcomeEnum;
  rootEventId?: string;
  employeeId?: string;
  employeeName?: string;
  lastAppointmentAt?: number;
  closedAt?: number;
  followUpCount: number;
  warnings: SeriesWarningEnum[];
  events: SeriesIndexEventRaw[];
  createdAt: number;
  updatedAt: number;
};

// Store-local shape after the thunk projects
// `dateTime` → `{ date, time }` per the store's timezone.
export type SeriesIndexDocument = {
  id: string;
  type: 'series';
  seriesId: string;
  enterpriseId: string;
  storeId: string;
  customerId: string;
  customerName: string;
  customerLastName: string;
  customerPhoneNumber?: string;
  customerEmail?: string;
  status: SeriesStatusEnum;
  outcome?: SeriesOutcomeEnum;
  rootEventId?: string;
  employeeId?: string;
  employeeName?: string;
  lastAppointmentAt?: number;
  closedAt?: number;
  followUpCount: number;
  warnings: SeriesWarningEnum[];
  events: SeriesIndexEvent[];
  createdAt: number;
  updatedAt: number;
};

export type GetSeriesByIdPayload = {
  id: string;
};

export type UpdateSeriesPayload = {
  id: string;
  note?: string;
};

export type RecordSeriesOutcomePayload = {
  id: string;
  outcome: SeriesOutcomeEnum;
};

// `outcome: null` is the undo signal for the per-appointment undo flow
// (LT-4074) — reuses the record-outcome route with a null body.
export type RecordEventOutcomePayload = {
  eventId: string;
  outcome: number | null;
};

// Unified outcome choice from the modal
export enum OutcomeChoice {
  BOUGHT = 'BOUGHT',
  FAILED = 'FAILED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export const isSeriesOutcome = (c: OutcomeChoice): boolean => c === OutcomeChoice.BOUGHT || c === OutcomeChoice.FAILED;

export type SearchSeriesPayload = {
  q?: string;
  warnings?: SeriesWarningEnum[];
  sort?: 'name' | 'lastAppointmentAt';
  archived?: boolean;
};

export type SearchSeriesData = {
  byId: Record<string, SeriesIndexDocument>;
  order: string[];
  archivedCount: number;
};

// Thunk success payload — store-local shape after `dateTime` is projected
// into the `{ date, time }` pair the timeline helpers consume.
export type SearchSeriesResponse = {
  items: SeriesIndexDocument[];
  archivedCount: number;
};
